import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";

import { Input } from "@/components/ui/Input";
import {
  formatDraftUnitPriceDisplay,
  sanitizeDraftUnitPriceInput,
} from "@/lib/purchaseOrderDraftForm";
import { cn } from "@/lib/utils";

import { DeferredCommitFieldFrame } from "./DeferredCommitFieldFrame";
import {
  deferredCommitControlClassName,
  deferredCommitOverlayGlyphPadding,
  useDeferredCommitFieldFeedback,
} from "./deferredCommitFieldShared";
import {
  deferredCommitStatusGlyphVisible,
} from "./DeferredCommitStatusGlyph";

function normalizeDeferredCommitUnitPrice(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed === "") {
    return null;
  }
  const parsed = Number.parseFloat(trimmed);
  if (!Number.isFinite(parsed) || parsed < 0) {
    return null;
  }
  return parsed;
}

function unitPricesEqual(left: number | null, right: number | null): boolean {
  if (left == null && right == null) {
    return true;
  }
  if (left == null || right == null) {
    return false;
  }
  return Math.abs(left - right) < 1e-9;
}

/** Unit price field that commits on blur for saved purchase order lines. */
export function DeferredCommitUnitPriceInput({
  value,
  disabled,
  pending,
  commitError,
  commitSucceeded,
  statusGlyphClassName,
  className,
  label,
  onCommit,
}: {
  value: string;
  disabled: boolean;
  pending?: boolean;
  commitError?: string | null;
  commitSucceeded?: boolean;
  statusGlyphClassName?: string;
  className?: string;
  label?: ReactNode;
  onCommit: (value: number) => void;
}): JSX.Element {
  const [draft, setDraft] = useState(value);
  const [focused, setFocused] = useState(false);
  const skipNextBlurCommitRef = useRef(false);

  useEffect(() => {
    setDraft(value);
  }, [value]);

  const isDirty = useMemo(() => {
    return !unitPricesEqual(normalizeDeferredCommitUnitPrice(draft), normalizeDeferredCommitUnitPrice(value));
  }, [draft, value]);

  const flashTone = useDeferredCommitFieldFeedback(commitSucceeded, commitError);
  const showOverlayGlyph = deferredCommitStatusGlyphVisible({
    pending,
    isDirty,
    commitError,
  });

  const commitDraft = useCallback((): void => {
    if (pending === true) {
      return;
    }
    const next = normalizeDeferredCommitUnitPrice(draft);
    const current = normalizeDeferredCommitUnitPrice(value);
    if (next == null) {
      return;
    }
    if (!unitPricesEqual(next, current)) {
      onCommit(next);
    }
  }, [draft, onCommit, pending, value]);

  const displayValue = focused ? draft : formatDraftUnitPriceDisplay(draft);

  return (
    <DeferredCommitFieldFrame
      label={label}
      pending={pending}
      isDirty={isDirty}
      disabled={disabled}
      commitError={commitError}
      commitSucceeded={commitSucceeded}
      showOverlayGlyph={showOverlayGlyph}
    >
      <Input
        type="text"
        inputMode="decimal"
        autoComplete="off"
        value={displayValue}
        disabled={disabled}
        aria-busy={pending === true}
        onFocus={() => setFocused(true)}
        onChange={(event) => {
          setDraft(sanitizeDraftUnitPriceInput(event.target.value));
        }}
        onBlur={() => {
          setFocused(false);
          setDraft(formatDraftUnitPriceDisplay(draft));
          if (skipNextBlurCommitRef.current) {
            skipNextBlurCommitRef.current = false;
            return;
          }
          commitDraft();
        }}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.preventDefault();
            commitDraft();
          }
          if (event.key === "Escape") {
            skipNextBlurCommitRef.current = true;
            setDraft(value);
            setFocused(false);
            event.currentTarget.blur();
          }
        }}
        className={cn(
          "min-w-0 shrink-0",
          deferredCommitOverlayGlyphPadding(showOverlayGlyph && !label),
          className,
          deferredCommitControlClassName({
            isDirty,
            disabled,
            pending,
            commitError,
            flashTone,
          }),
          statusGlyphClassName,
        )}
      />
    </DeferredCommitFieldFrame>
  );
}
